import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheckIcon,
  UsersIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  UserGroupIcon,
  CheckIcon,
  StarIcon as StarIconOutline,
} from "@heroicons/react/24/outline";
import {
  StarIcon as StarIconSolid,
  ChatBubbleBottomCenterTextIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import HomeNav from "../navigations/homenav";
import Footer from "../navigations/Footer";
import { useLanguageContext } from "../navigations/homenav";
import axios from "axios";
import { Helmet } from "react-helmet-async";


// Use .env if possible: REACT_APP_NEWSAPI_KEY=your_key_here
const NEWSAPI_KEY =
  process.env.REACT_APP_NEWSAPI_KEY ;

// Feature Card Component
const FeatureCard = ({ icon, title, children }) => (
  <div className="card-bg p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300">
    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-sky-100 text-sky-600">
      {icon}
    </div>
    <h3
      className="mt-5 text-lg font-semibold"
      style={{ color: "var(--text-primary)" }}
    >
      {title}
    </h3>
    <p className="mt-2 text-base" style={{ color: "var(--text-secondary)" }}>
      {children}
    </p>
  </div>
);

// Testimonial Card Component
const TestimonialCard = ({ quote, name, title, avatar, rating }) => {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(
      i < rating ? (
        <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />
      ) : (
        <StarIconOutline key={i} className="h-5 w-5 text-yellow-400" />
      )
    );
  }

  return (
    <div className="card-bg p-8 rounded-lg shadow-lg relative text-center">
      <p className="text-gray-600 italic">"{quote}"</p>
      <div className="mt-6 flex flex-col items-center">
        <img
          src={avatar}
          alt={name}
          className="w-16 h-16 rounded-full object-cover"
        />
        <div className="mt-4">
          <p className="font-semibold" style={{ color: "var(--text-primary)" }}>
            {name}
          </p>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {title}
          </p>
          <div className="flex justify-center mt-2">{stars}</div>
        </div>
      </div>
      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 card-bg p-3 rounded-full shadow-md">
        <ChatBubbleBottomCenterTextIcon className="h-6 w-6 text-sky-600 transform rotate-180" />
      </div>
    </div>
  );
};

const partners = [
  { name: "Governmental", logo: "/governmentlogo.png" },
  { name: "CSOs", logo: "/civilsocietylogo.png" },
  { name: "Community Based Associations", logo: "/communitylogo.jpg" },
  { name: "ZOA", logo: "/zoalogo.png" },
  { name: "We are Able", logo: "/we-are-able.png" },
  { name: "DPO networks", logo: "/dpologo.png" },
  { name: "International Partners", logo: "/internationalpartners.png" },
];

const HomePage = () => {
  const { t } = useLanguageContext();
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);
  const scrollFunctions = useRef({});
  // 🔍 Extra client-side filter for disability-related articles only
const DISABILITY_KEYWORDS = [
  "disability",
  "disabled",
  "persons with disabilities",
  "people with disabilities",
  "PWD",
  "wheelchair",
  "assistive technology",
  "inclusive education",
  "accessibility",
  "inclusive",
  "autism",
  "down syndrome",
  "intellectual disability",
  "hearing impairment",
  "deaf",
  "blind",
  "low vision",
  "rehabilitation",
  "prosthetic",
  "sign language"
];

const EXCLUDED_POLITICAL_KEYWORDS = [
  "election",
  "elections",
  "president",
  "prime minister",
  "parliament",
  "campaign",
  "vote",
  "voting",
  "senate",
  "congress",
  "republican",
  "democrat",
  "party",
  "coalition",
  "war",
  "conflict",
  "gaza",
  "israel",
  "ukraine",
  "russia",
  "trump",
  "biden",
  "politics",
  "political"
];

// article here is the *mapped* article you render in the cards
const isDisabilityRelevant = (article) => {
  const text = `${article.title || ""} ${article.excerpt || ""}`.toLowerCase();

  const hasDisabilityKeyword = DISABILITY_KEYWORDS.some((kw) =>
    text.includes(kw.toLowerCase())
  );

  const hasPoliticalNoise = EXCLUDED_POLITICAL_KEYWORDS.some((kw) =>
    text.includes(kw.toLowerCase())
  );

  // keep only if it clearly mentions disability AND is not obviously political
  return hasDisabilityKeyword && !hasPoliticalNoise;
};


  const scrollStep = 1; // Pixels to scroll per interval
  const scrollInterval = 35; // Interval in ms (lower is faster)

  useEffect(() => {
    // Define the functions that reference each other
    scrollFunctions.current.stopScroll = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    scrollFunctions.current.startScroll = () => {
      scrollFunctions.current.stopScroll(); // Use the ref to avoid closure issues
      intervalRef.current = setInterval(() => {
        if (carouselRef.current) {
          const { scrollLeft, scrollWidth } = carouselRef.current;
          const scrollBoundary = scrollWidth / 2;


          // If scrolled past the first set of logos, jump back
          if (scrollLeft >= scrollBoundary) {
            carouselRef.current.style.scrollBehavior = "auto";
            carouselRef.current.scrollLeft = scrollLeft - scrollBoundary;
            carouselRef.current.style.scrollBehavior = "smooth";
          }
          carouselRef.current.scrollLeft += scrollStep;
        }
      }, scrollInterval);
    };

    // Start the scroll when component mounts
    scrollFunctions.current.startScroll();

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [scrollStep, scrollInterval]);

  const handleNavClick = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 208; // Corresponds to w-52
      carouselRef.current.scrollLeft +=
        direction === "next" ? scrollAmount : -scrollAmount;
    }
  };

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    { src: "/galleryimagemain1.jpg", alt: "Community Event 1" },
    { src: "/homeimagemain2.jpg", alt: "Training Program" },
    { src: "/homeimagemain7.jpg", alt: "Advocacy Meeting" },
    { src: "/galleryimagemain4.jpg", alt: "Support Services" },
    { src: "/homeimagemain.jpeg", alt: "Educational Workshop" },
    { src: "/galleryimagemain6.jpg", alt: "Community Gathering" },
    { src: "/galleryimagemain7.jpg", alt: "Community Gathering" },
    { src: "/galleryimagemain8.png", alt: "Community Gathering" },
    { src: "/homeimagemain3.jpg", alt: "Community Gathering" },
  ];

  const nextImage = () => {
    setCurrentImageIndex(
      (prev) => (prev + 1) % Math.max(1, galleryImages.length - 3)
    );
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) =>
        (prev - 1 + Math.max(1, galleryImages.length - 3)) %
        Math.max(1, galleryImages.length - 3)
    );
  };

  const openModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const visibleImages = galleryImages.slice(
    currentImageIndex,
    currentImageIndex + 4
  );

  // Hero slider images (ensure these exist in /public). You can replace with preferred assets.
  const heroImages = [
    "/homeimagemain6.jpg",
    "/homeimagemain2.jpg",
    "/homeimagemain8.jpg",
  ];
  const [heroIndex, setHeroIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(id);
  }, [heroImages.length]);

  // 🔹 Global Disability News state for landing page
  const [globalDisNews, setGlobalDisNews] = useState([]);
  const [loadingGlobalNews, setLoadingGlobalNews] = useState(true);

  useEffect(() => {

  const mapArticle = (a) => ({
    id: a.url,
    title: a.title,
    excerpt: a.description || a.content || "",
    date: a.publishedAt ? new Date(a.publishedAt).toLocaleDateString() : "",
    author: (a.source && a.source.name) || "News source",
    image: a.urlToImage || "/newsfallback.jpg",
    url: a.url,
  });

  const fetchGlobalDisabilityNews = async () => {
    try {
      // Fetch from backend which proxies NewsAPI
      const res = await fetch("https://aaapd.org.et/gallery_backend/api/news.php");
      const json = await res.json();

      // Support multiple possible shapes from the backend
      let rawArticles = [];
      if (Array.isArray(json)) {
        rawArticles = json;
      } else if (Array.isArray(json.articles)) {
        rawArticles = json.articles;
      } else if (Array.isArray(json.data)) {
        rawArticles = json.data;
      }

      // Map to UI structure with robust fallbacks
      const mapped = rawArticles.map((a) => ({
        id: a.id || a.url || a.link || Math.random().toString(36).slice(2),
        title: a.title || a.headline || "",
        excerpt: a.excerpt || a.description || a.content || "",
        date: (() => {
          const d = a.date || a.publishedAt || a.published_at;
          return d ? new Date(d).toLocaleDateString() : "";
        })(),
        author: a.author || (a.source && (a.source.name || a.source)) || "News source",
        image: a.image || a.urlToImage || a.thumbnail || "/newsfallback.jpg",
        url: a.url || a.link || "#",
      }));

      // Optional extra client-side filter to keep disability-focused items
      const filtered = mapped.filter(isDisabilityRelevant);

      setGlobalDisNews(filtered);
    } catch (err) {
      console.error("Error fetching global disability news:", err);
      setGlobalDisNews([]);
    } finally {
      setLoadingGlobalNews(false);
    }
  };

  fetchGlobalDisabilityNews();
}, []);



  return (
    <div
      style={{
        backgroundColor: "var(--bg-secondary)",
        color: "var(--text-primary)",
      }}
    >
      <style>
        {`
          @keyframes scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-100%); }
          }
          .scrolling-logos {
            display: flex;
            width: calc(200px * ${partners.length * 2});
            animation: scroll 40s linear infinite;
          }
          .scrolling-logos:hover {
            animation-play-state: paused;
          }
          .logo-item {
            width: 200px;
          }
        `}
      </style>
      {/* ← Helmet for SEO */}
      <Helmet>
        <title>Addis Ababa Association of Persons with Disabilities | AAAPD</title>
        <meta
          name="description"
          content="Empowering persons with disabilities through innovative programs and inclusive community building in Addis Ababa."
        />
        <link rel="canonical" href="https://aaapd.org.et/" />
      </Helmet>

      <HomeNav />

      <main id="main">
        <div className="relative">
          {/* Sticky Hero Background (Slider) */}
          <div className="sticky top-0 h-screen relative overflow-hidden">
            {heroImages.map((src, i) => (
              <div
                key={src + i}
                className={
                  "absolute inset-0 bg-cover bg-center transition-opacity duration-700 " +
                  (i === heroIndex ? "opacity-100" : "opacity-0")
                }
                style={{ backgroundImage: `url('${src}')` }}
                aria-hidden={i !== heroIndex}
              />
            ))}
          </div>

          {/* Scrollable Content Wrapper */}
          <div className="relative z-10" style={{ marginTop: "-100vh" }}>
            {/* Hero Content Section (scrolls) */}
            <section className="h-screen flex flex-col justify-center text-center text-white px-6">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight">
                {t("heroTitle")}
              </h1>
              <div className="mt-2 text-4xl md:text-6xl font-extrabold tracking-tight">(AAAPD)</div>
              <p className="mt-6 max-w-3xl mx-auto text-lg md:text-xl text-white font-medium
             drop-shadow-[0_2px_16px_rgba(0,0,0,0.95)]">
  {t("heroSubtitle")}
</p>

              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Link
                  to="/contactus"
                  className="px-8 py-3 rounded-md text-lg font-medium bg-sky-600 hover:bg-sky-700 transition-colors duration-300"
                >
                  {t("accessDashboard")}
                </Link>
                <a
                  href="/whoweare"
                  className="px-8 py-3 rounded-md text-lg font-medium bg-white text-sky-700 hover:bg-gray-100 transition-colors duration-300"
                >
                  {t("learnMore")}
                </a>
              </div>
            </section>

            {/* About Us Section */}
            <section
              id="about"
              className="py-20"
              style={{ backgroundColor: "var(--bg-secondary)" }}
            >
              <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
                {/* Image Collage */}
                <div className="relative h-96 md:h-auto md:aspect-[4/3]">
                  <img
                    src="/aboutimagemain5.jpg"
                    alt="Community gathering"
                    className="absolute top-0 left-0 w-2/3 rounded-lg shadow-lg transform -rotate-3 transition-transform duration-300 hover:rotate-0"
                  />
                  <img
                    src="/aboutimagemain4.jpg"
                    alt="Smiling child"
                    className="absolute bottom-0 right-0 w-1/2 rounded-lg shadow-2xl border-4 border-white transform rotate-3 transition-transform duration-300 hover:rotate-0"
                  />
                </div>


                {/* Content */}
                <div>
                  <p className="text-sm font-semibold text-sky-600 uppercase">
                    {t("aboutMission")}
                  </p>
                  <h2
                    className="mt-2 text-3xl md:text-4xl font-bold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {t("aboutTitle")}
                  </h2>
                  <p
                    className="mt-4 text-lg"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {t("aboutDescription")}
                  </p>
                  <div className="mt-8 bg-sky-50 rounded-lg p-6 flex flex-col md:flex-row gap-6 text-center">
                    <div className="flex-1">
                      <HeartIcon className="h-8 w-8 mx-auto text-sky-600" />
                      <h4 className="mt-2 font-semibold text-gray-800">
                        {t("promoteAccess")}
                      </h4>
                    </div>
                    <div className="border-t md:border-t-0 md:border-l border-sky-200"></div>
                    <div className="flex-1">
                      <UserGroupIcon className="h-8 w-8 mx-auto text-sky-600" />
                      <h4 className="mt-2 font-semibold text-gray-800">
                        {t("connectOPDs")}
                      </h4>
                    </div>
                  </div>
                  <ul className="mt-6 space-y-2">
                    <li className="flex items-center text-gray-700">
                      <CheckIcon className="h-5 w-5 mr-2 text-green-500" />
                      {t("supportingMembers")}
                    </li>
                    <li className="flex items-center text-gray-700">
                      <CheckIcon className="h-5 w-5 mr-2 text-green-500" />
                      {t("buildingNetwork")}
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Features Section */}
            <section
              id="features"
              className="py-20"
              style={{ backgroundColor: "var(--bg-primary)" }}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div className="text-center">
                  <h2
                    className="text-3xl md:text-4xl font-bold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {t("featuresTitle")}
                  </h2>
                  <p
                    className="mt-4 text-lg"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {t("featuresSubtitle")}
                  </p>
                </div>
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <FeatureCard
                    icon={<UsersIcon className="h-6 w-6" />}
                    title={t("memberManagement")}
                  >
                    {t("memberManagementDesc")}
                  </FeatureCard>
                  <FeatureCard
                    icon={<ChartBarIcon className="h-6 w-6" />}
                    title={t("supportTrackings")}
                  >
                    {t("supportTrackingDesc")}
                  </FeatureCard>
                  <FeatureCard
                    icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
                    title={t("secureCommunication")}
                  >
                    {t("secureCommunicationDesc")}
                  </FeatureCard>
                  <FeatureCard
                    icon={<ShieldCheckIcon className="h-6 w-6" />}
                    title={t("dataReporting")}
                  >
                    {t("dataReportingDesc")}
                  </FeatureCard>
                  <FeatureCard
                    icon={<ShieldCheckIcon className="h-6 w-6" />}
                    title={t("dataReporting2")}
                  >

                    {t("dataReportingDesc2")}
                  </FeatureCard>
                  <FeatureCard
                    icon={<ShieldCheckIcon className="h-6 w-6" />}
                    title={t("dataReporting3")}
                  >
                    {t("dataReportingDesc3")}
                  </FeatureCard>
                </div>
              </div>
            </section>

            {/* Social Proof/Impact Section with Sticky Background */}
            <section
              className="py-20 bg-cover bg-center bg-fixed relative"
              style={{ backgroundImage: "url('/homeimage3.png')" }}
            >
              <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>
              <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
                <h2 className="text-3xl font-bold text-white">
                  {t("trustedTitle")}
                </h2>
                <p className="mt-3 text-lg text-sky-100">
                  {t("trustedSubtitle")}
                </p>
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8">
                  <div className="text-center">
                    <div className="text-4xl font-extrabold text-white">11</div>
                    <div className="mt-2 text-base text-sky-200">
                      {t("opdsPartnered")}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-extrabold text-white">
                      10,000+
                    </div>
                    <div className="mt-2 text-base text-sky-200">
                      {t("membersSupported")}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-extrabold text-white">
                      15+
                    </div>
                    <div className="mt-2 text-base text-sky-200">
                      {t("projectsImplemented")}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-extrabold text-white">
                      {t("offlineFirst")}
                    </div>
                    <div className="mt-2 text-base text-sky-200">
                      {t("reliableAccessible")}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Video Section */}
            <section id="video" className="py-20 bg-white">
              <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-12">
                  <h2
                    className="text-3xl md:text-4xl font-bold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {t("featuresTitle2")}
                  </h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                    {t("featuresTitle3")}
                  </p>
                </div>


                <div className="max-w-4xl mx-auto">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-black">
                    <video
                      ref={(video) => {
                        if (video) {
                          const observer = new IntersectionObserver(
                            (entries) => {
                              entries.forEach((entry) => {
                                if (entry.isIntersecting) {
                                  video.play().catch((e) => {
                                    // Handle autoplay failure silently
                                    console.log("Autoplay prevented:", e);
                                  });
                                } else {
                                  video.pause();
                                }
                              });
                            },
                            { threshold: 0.5 }
                          );
                          observer.observe(video);
                        }
                      }}
                      className="w-full h-auto"
                      controls
                      preload="metadata"
                      muted
                    >
                      <source src="/homepagevideo.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <div className="mt-8 text-center">
                    <p className="text-gray-600">
                      {t("featuresTitle4")}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* 🔹 News Section – Global Disability Headlines */}
            <section
              id="news"
              className="py-20"
              style={{ backgroundColor: "var(--bg-primary)" }}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div className="text-center">
                  <h2
                    className="text-3xl md:text-4xl font-bold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {t("newsTitle")}
                  </h2>
                  <p
                    className="mt-4 text-lg"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {t("newsSubtitle")}
                  </p>
                  <p className="mt-2 text-xs uppercase tracking-wide text-sky-600">
                    🌍 Global disability news – powered by external sources
                  </p>
                </div>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
                  {loadingGlobalNews ? (
                    // Skeleton loaders while fetching
                    [...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="card-bg rounded-lg shadow-md overflow-hidden animate-pulse"
                      >
                        <div className="w-full h-48 bg-slate-300" />
                        <div className="p-6 space-y-3">
                          <div className="h-4 bg-slate-300 rounded w-3/4" />
                          <div className="h-3 bg-slate-200 rounded w-full" />
                          <div className="h-3 bg-slate-200 rounded w-5/6" />
                          <div className="h-8 bg-slate-300 rounded w-24 mt-4" />
                        </div>
                      </div>
                    ))
                  ) : globalDisNews.length ? (
                    globalDisNews.slice(0, 3).map((article) => (
                      <div
                        key={article.id}
                        className="card-bg rounded-lg shadow-md overflow-hidden"
                      >
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-48 object-cover"
                        />
                        <div className="p-6">
                          <h3
                            className="text-lg font-semibold line-clamp-2"
                            style={{ color: "var(--text-primary)" }}
                          >
                            {article.title}
                          </h3>
                          <p
                            className="mt-2 text-base line-clamp-3"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {article.excerpt}
                          </p>

                          <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                            <span className="truncate pr-2">
                              {article.author}
                            </span>
                            <span>{article.date}</span>
                          </div>


                          <a
                            href={article.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-block text-sky-600 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded"
                          >
                            {t("readMore")}
                          </a>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p
                      className="col-span-1 md:col-span-3 text-center text-sm"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      No recent global disability news available at the moment.
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Partners Section - Continuous Scroll */}
            <section
              id="partners"
              className="py-12"
              style={{ backgroundColor: "var(--bg-primary)" }}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div
                  className="relative group"
                  onMouseEnter={scrollFunctions.current.stopScroll}
                  onMouseLeave={scrollFunctions.current.startScroll}
                >
                  <div
                    ref={carouselRef}
                    className="flex overflow-x-hidden"
                    style={{ scrollBehavior: "smooth" }}
                  >
                    {[...partners, ...partners].map((partner, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 w-52 flex items-center justify-center px-4"
                      >
                        <img
                          src={partner.logo}
                          alt={partner.name}
                          className="h-12 mx-auto"
                        />
                      </div>
                    ))}
                  </div>
                  {/* Navigation Arrows */}
                  <div className="absolute inset-0 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <button
                      onClick={() => handleNavClick("prev")}
                      className="p-2 bg-white/50 rounded-full shadow-md hover:bg-white pointer-events-auto"
                    >
                      <ChevronLeftIcon className="h-6 w-6 text-gray-700" />
                    </button>
                    <button
                      onClick={() => handleNavClick("next")}
                      className="p-2 bg-white/50 rounded-full shadow-md hover:bg-white pointer-events-auto"
                    >
                      <ChevronRightIcon className="h-6 w-6 text-gray-700" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Gallery Section */}
            <section
              className="py-20"
              style={{ backgroundColor: "var(--bg-secondary)" }}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div className="text-center">
                  <h2
                    className="text-3xl md:text-4xl font-bold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {t("galleryTitle")}
                  </h2>
                  <p
                    className="mt-4 text-lg"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {t("gallerySubtitle")}
                  </p>
                </div>


                {/* Gallery Carousel */}
                <div className="mt-16 relative">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {visibleImages.map((image, index) => (
                      <div
                        key={index}
                        className="overflow-hidden rounded-lg shadow-md cursor-pointer transform hover:scale-105 transition-transform duration-300"
                        onClick={() => openModal(image)}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Navigation Arrows */}
                  {galleryImages.length > 4 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
                        aria-label="Previous images"
                      >
                        <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50 transition-colors"
                        aria-label="Next images"
                      >
                        <ChevronRightIcon className="h-6 w-6 text-gray-600" />
                      </button>
                    </>
                  )}
                </div>

                {/* View More Button */}
                <div className="flex justify-end mt-8">
                  <Link
                    to="/gallery"
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-sky-600 text-white hover:bg-sky-700 transition-colors duration-300 shadow-md"
                  >
                    {t("viewMore")}
                  </Link>
                </div>
              </div>
            </section>

            {/* Image Modal */}
            {isModalOpen && selectedImage && (
              <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                <div className="relative max-w-2xl max-h-[70vh]">
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="max-w-full max-h-[60vh] object-contain rounded-lg"
                  />
                  <button
                    onClick={closeModal}
                    className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors"
                    aria-label="Close modal"
                  >
                    <XMarkIcon className="h-6 w-6 text-gray-600" />
                  </button>
                </div>
              </div>
            )}


            {/* Contact Us Section - Redesigned */}
            <section
              id="contact"
              className="py-20 text-white bg-cover bg-center bg-fixed relative"
              style={{ backgroundImage: "url('/homeform3.jpg')" }}
            >
              <div className="absolute inset-0 bg-gray-900 bg-opacity-75"></div>
              <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center relative z-10">
                {/* Left Column: Image & Overlay */}
                <div className="relative flex justify-center items-center">
                  <div className="relative w-80 h-80">
                    <img
                      src="/homeform4.jpeg"
                      alt="Community member"
                      className="rounded-full w-full h-full object-cover border-4 border-sky-400 shadow-2xl transition-transform duration-300 ease-in-out hover:scale-105"
                    />
                    <div className="absolute -bottom-8 -left-8 bg-white text-gray-900 p-6 rounded-lg shadow-xl">
                      <p className="text-4xl font-bold text-sky-600">10k+</p>
                      <p className="text-sm font-semibold">
                        {t("livesImpacted")}
                      </p>
                    </div>
                    {/* Decorative elements */}
                    <div className="absolute -top-4 -right-4 w-16 h-16 border-4 border-sky-500 rounded-full opacity-50"></div>
                    <div className="absolute bottom-0 -right-12 w-20 h-20 bg-orange-500 rounded-full opacity-25"></div>
                  </div>
                </div>

                {/* Right Column: Form */}
<div>
  <h2 className="text-3xl font-bold text-white">
    {t("contactTitle")}
  </h2>
  <p className="mt-4 text-lg text-gray-300">
    {t("contactSubtitle")}
  </p>

  {/* ✅ Formspree-powered form */}
  <form
    className="mt-8 space-y-6 bg-white p-8 rounded-lg shadow-2xl"
    action="https://formspree.io/f/xyzooadj"
    method="POST"
  >
    {/* Name */}
    <div>
      <input
        type="text"
        id="full-name"
        name="name"
        required
        placeholder={t("yourName")}
        className="mt-2 block w-full bg-gray-100 border-transparent rounded-full shadow-sm py-3 px-6 text-gray-700 focus:border-sky-500 focus:ring-sky-500"
      />
    </div>

    {/* Email */}
    <div>
      <input
        type="email"
        id="email-address"
        name="email"
        required
        placeholder={t("emailAddress")}
        className="mt-2 block w-full bg-gray-100 border-transparent rounded-full shadow-sm py-3 px-6 text-gray-700 focus:border-sky-500 focus:ring-sky-500"
      />
    </div>

    {/* Phone */}
    <div>
      <input
        type="tel"
        id="phone-number"
        name="phone"
        placeholder={t("phoneNumber")}
        className="mt-2 block w-full bg-gray-100 border-transparent rounded-full shadow-sm py-3 px-6 text-gray-700 focus:border-sky-500 focus:ring-sky-500"
      />
    </div>

    {/* Message */}
    <div>
      <textarea
        id="message"
        name="message"
        rows="4"
        required
        placeholder={t("typeMessage")}
        className="mt-2 block w-full bg-gray-100 border-transparent rounded-2xl shadow-sm py-3 px-6 text-gray-700 focus:border-sky-500 focus:ring-sky-500"
      ></textarea>
    </div>

    {/* Optional: custom subject so your email looks nicer */}
    <input type="hidden" name="_subject" value="New contact from AAAPD landing page" />

    {/* Submit */}
    <div>
      <button
        type="submit"
        className="w-full px-8 py-3 rounded-full text-lg font-semibold bg-teal-600 text-white hover:bg-teal-700 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-teal-500"
      >
        {t("sendMessage")}
      </button>
    </div>
  </form>
</div>

              </div>
            </section>

            {/* CTA Section */}
            <section style={{ backgroundColor: "var(--bg-primary)" }}>
              <div className="max-w-4xl mx-auto px-6 text-center -mt-16 relative z-20">
                <div className="bg-white rounded-lg shadow-xl p-8">
                  <h2
                    className="text-3xl font-bold"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {t("homeCtaTitle")}
                  </h2>
                  <p
                    className="mt-4 text-lg"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {t("homeCtaDesc")}
                  </p>
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        // Trigger the self-registration modal directly
                        document
                          .querySelector(
                            'button[aria-label*="openSelfRegistrationForm"]'
                          )
                          ?.click();
                      }}
                      className="px-8 py-3 rounded-md text-lg font-medium text-white transition-colors duration-300"
                      style={{ backgroundColor: "#8b4513" }}
                      onMouseEnter={(e) =>
                        (e.target.style.backgroundColor = "#a0522d")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.backgroundColor = "#8b4513")
                      }
                    >
                      {t("homeCtaButton")}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
